import {
  fetchAssignments,
  fetchCatalog,
  fetchPermissionSets,
  fetchUsers,
} from '@/features/user-management/api/authorization';
import { normalizeUserDirectoryQuery } from '@/features/user-management/lib/userDirectorySearch';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';

const USER_MANAGEMENT_STALE_TIME = 30_000;

export const userManagementKeys = {
  all: ['user-management'] as const,
  directories: () => [...userManagementKeys.all, 'directory'] as const,
  directory: (q?: string) => [...userManagementKeys.directories(), q?.trim() ?? ''] as const,
  user: (uid: string) => [...userManagementKeys.directories(), 'user', uid] as const,
  permissionSets: () => [...userManagementKeys.all, 'permission-sets'] as const,
  catalog: () => [...userManagementKeys.all, 'catalog'] as const,
  accesses: () => [...userManagementKeys.all, 'access'] as const,
  access: (principalId: string) => [...userManagementKeys.all, 'access', principalId] as const,
};

export function userManagementDirectoryQuery(q?: string) {
  const term = normalizeUserDirectoryQuery(q);
  return infiniteQueryOptions({
    queryKey: userManagementKeys.directory(term),
    queryFn: ({ pageParam, signal }) => fetchUsers({ cursor: pageParam, q: term }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: USER_MANAGEMENT_STALE_TIME,
  });
}

export function useUserManagementDirectory(q?: string) {
  return useInfiniteQuery(userManagementDirectoryQuery(q));
}

export function userManagementUserQuery(uid: string) {
  return queryOptions({
    queryKey: userManagementKeys.user(uid),
    queryFn: async ({ signal }) => {
      let cursor: string | undefined;
      const visitedCursors = new Set<string>();

      while (true) {
        const page = await fetchUsers({ cursor }, { signal });
        const user = page.items.find((candidate) => candidate.uid === uid);
        if (user) return user;

        const nextCursor = page.nextCursor ?? undefined;
        if (!nextCursor || visitedCursors.has(nextCursor)) return null;
        visitedCursors.add(nextCursor);
        cursor = nextCursor;
      }
    },
    staleTime: USER_MANAGEMENT_STALE_TIME,
  });
}

export const userManagementPermissionSetsQuery = queryOptions({
  queryKey: userManagementKeys.permissionSets(),
  queryFn: ({ signal }) => fetchPermissionSets({ signal }),
  staleTime: USER_MANAGEMENT_STALE_TIME,
});

export const userManagementCatalogQuery = queryOptions({
  queryKey: userManagementKeys.catalog(),
  queryFn: ({ signal }) => fetchCatalog({ signal }),
  staleTime: USER_MANAGEMENT_STALE_TIME,
});

export function userManagementAccessQuery(principalId: string) {
  return queryOptions({
    queryKey: userManagementKeys.access(principalId),
    queryFn: ({ signal }) => fetchAssignments(principalId, { signal }),
    staleTime: 0,
  });
}
